import {
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Select, AsyncSelect } from 'chakra-react-select';
import FormikFormControl from 'components/common/forms/FormikFormControl';
import axios from 'axios';
import debounce from 'lodash.debounce';
import LoadingSpinner from 'components/common/loaders/LoadingSpinner';
import ConfirmationActionModal from 'components/common/modals/ConfirmationActionModal';

import { FaFileCsv } from 'react-icons/fa';
import { MdFolderZip } from 'react-icons/md';

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { subQuestReportActions } from 'store/modules/subQuestReport/subQuest-report-slice';
import {
  getBulkFileSubmissionsAction,
  getProgressionUserIdsAction,
} from 'store/modules/subQuestReport/subQuest-report-action';
import { statusOptions } from 'utils/constants/reportStatus';
import {
  IDLE,
  LOADING,
  SUCCEEDED,
  FAILED,
} from 'utils/constants/reducerStatus';
import { ReducerStatus } from 'utils/constants';
import exportAsCsv from 'utils/exportCsv';
import exportAsZip from 'utils/exportZip';

function ExportFileSessionsMd() {
  const dispatch = useDispatch();

  const helperTextColor = useColorModeValue('gray', '#c0bdbd');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');
  const cardBg = useColorModeValue('white', 'gray.800');

  const exportSubmissionSessions = useSelector(
    (state) => state.subQuestReport.exportSubmissionSessions
  );

  const [summaryData, setSummaryData] = useState(null);
  const [showSummary, setShowSummary] = useState(true);

  const [showExportConfirmation, setShowExportConfirmation] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [exportType, setExportType] = useState(null); // 'CSV' or 'ZIP'
  const [exportStatus, setExportStatus] = useState(IDLE);
  const [zipProgress, setZipProgress] = useState({ current: 0, total: 0 });
  const [zipStats, setZipStats] = useState(null);

  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedQuest, setSelectedQuest] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const loadCourses = debounce((inputValue, callback) => {
    axios
      .get(`/courses?query=${inputValue.trim()}&pageSize=5`)
      .then((response) => {
        callback(
          response.data.data?.map((course) => ({
            label: course.name,
            value: course.id,
          }))
        );
      })
      .catch(() => {
        callback([]);
      });
  }, 1000);

  const loadQuests = debounce((inputValue, callback) => {
    axios
      .get(`/admin/quests?query=${inputValue.trim()}&pageSize=5`)
      .then((response) => {
        callback(
          response.data.data?.map((quest) => ({
            label: quest.name,
            value: quest.id,
          }))
        );
      })
      .catch(() => {
        callback([]);
      });
  }, 1000);

  const handleCourseChange = (selectedOption) => {
    setSelectedCourse(selectedOption);
  };

  const handleQuestChange = (selectedOption) => {
    setSelectedQuest(selectedOption);
  };

  const handleStatusChange = (selectedOptions) => {
    setSelectedStatus(selectedOptions);
  };

  /**
   * Fetch bulk file submissions when modal opens
   * Dispatches action with userIds and subQuestId from Redux state
   */
  useEffect(() => {
    if (
      exportSubmissionSessions.modal &&
      exportSubmissionSessions.userIds?.length > 0 &&
      exportSubmissionSessions.subQuestId
    ) {
      dispatch(
        getBulkFileSubmissionsAction({
          subQuestId: exportSubmissionSessions.subQuestId,
          userIds: exportSubmissionSessions.userIds,
        })
      );
    }
  }, [
    exportSubmissionSessions.modal,
    exportSubmissionSessions.userIds,
    exportSubmissionSessions.subQuestId,
    dispatch,
  ]);

  /**
   * Generate summary data from the fetched file submissions
   */
  useEffect(() => {
    if (
      exportSubmissionSessions.status === ReducerStatus.SUCCEEDED &&
      exportSubmissionSessions.data
    ) {
      const { data } = exportSubmissionSessions;
      const totalRecords = data.length;

      // Count status breakdown
      const statusCounts = {};
      data.forEach((record) => {
        const status = record.result || 'Not Started';
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      });

      const statusBreakdown = Object.entries(statusCounts).map(
        ([status, count]) => {
          const percentage = Math.round((count / totalRecords) * 100);
          let colorScheme = 'gray';
          if (status === 'Pass' || status === 'Completed') {
            colorScheme = 'green';
          } else if (status === 'For Checking' || status === 'Pending') {
            colorScheme = 'yellow';
          } else if (status === 'Fail') {
            colorScheme = 'red';
          }
          return { status, count, percentage, colorScheme };
        }
      );

      setSummaryData({
        totalRecords,
        statusBreakdown,
        typeBreakdown: [{ type: 'File Submission', count: totalRecords }],
      });
    }
  }, [exportSubmissionSessions]);

  /**
   * Re-fetch data when filters are applied.
   * Uses a two-step process to bypass pagination:
   * 1. Fetch ALL user IDs matching the filters
   * 2. Fetch bulk file submissions for those user IDs
   */
  const handleApplyFilters = async () => {
    if (exportSubmissionSessions.subQuestId) {
      try {
        // Fetch ALL user IDs matching filters
        const userIdsResult = await dispatch(
          getProgressionUserIdsAction({
            subQuestId: exportSubmissionSessions.subQuestId,
            courseId: selectedCourse?.value,
            questId: selectedQuest?.value,
            results: selectedStatus?.map((option) => option.value) || null,
          })
        ).unwrap();

        // Fetch bulk file submissions for all filtered users
        dispatch(
          getBulkFileSubmissionsAction({
            subQuestId: exportSubmissionSessions.subQuestId,
            userIds: userIdsResult,
            courseId: selectedCourse?.value,
            questId: selectedQuest?.value,
            results: selectedStatus?.map((option) => option.value) || null,
          })
        );
      } catch (error) {
        setExportError(error);
      }
    }
  };

  /**
   * Transforms bulk file submission data for CSV export
   * Maps API response fields to CSV column headers and formats the data
   * @param {Array} data - Array of GetBulkFileSubmissionsResult objects from API
   * @returns {Array} - Transformed array with CSV-ready field names and formatted values
   */
  const transformDataForCsv = (data) => {
    return data.map((record) => ({
      FileSubmissionSessionId: record.fileSubmissionSessionId,
      SubQuestId: record.id,
      SubQuestName: record.name,
      UserId: record.userId,
      UserName: record.userName,
      Code: record.code,
      ScoreType: record.scoreType,
      TotalScore: record.totalScore,
      PassingScore: record.passingScore,
      FilePaths: record.filePaths?.join('; ') || '',
      Score: record.score,
      Result: record.result || '',
      Remarks: record.remarks || '',
      ShowRemarks: record.showRemarks,
    }));
  };

  const handleCloseExportModal = () => {
    setSelectedCourse(null);
    setSelectedQuest(null);
    setSelectedStatus(null);

    dispatch(
      subQuestReportActions.setExportSubmissionSessions({ modal: false })
    );
  };

  const handleExportCSV = () => {
    setExportType('CSV');
    setShowExportConfirmation(true);
    setExportStatus(IDLE);
  };

  const handleExportZipFile = () => {
    setExportType('ZIP');
    setShowExportConfirmation(true);
    setExportStatus(IDLE);
  };

  const handleCancelExport = () => {
    setShowExportConfirmation(false);
    setExportType(null);
    setExportStatus(IDLE);
  };

  /**
   * Handles progress updates during ZIP file creation
   * @param {number} current - Number of files downloaded so far
   * @param {number} total - Total number of files to download
   */
  const handleZipProgress = (current, total) => {
    setZipProgress({ current, total });
  };

  /**
   * Handles the confirmed export action based on the selected export type
   * For CSV: Transforms data and triggers actual CSV download
   * For ZIP: Downloads all files, creates ZIP structure, and triggers download
   */
  const handleConfirmExport = async () => {
    if (exportType === 'CSV') {
      if (exportSubmissionSessions.data) {
        const transformedData = transformDataForCsv(
          exportSubmissionSessions.data
        );
        exportAsCsv(transformedData, 'file-submissions');
        setExportStatus(SUCCEEDED);
      } else {
        setExportStatus(FAILED);
      }
    } else if (exportType === 'ZIP') {
      setExportStatus(LOADING);
      setZipProgress({ current: 0, total: 0 });
      setZipStats(null);

      try {
        if (exportSubmissionSessions.data) {
          const stats = await exportAsZip(
            exportSubmissionSessions.data,
            process.env.REACT_APP_FILE_URL,
            handleZipProgress
          );
          setZipStats(stats);
          setExportStatus(SUCCEEDED);
        } else {
          setExportStatus(FAILED);
        }
      } catch (error) {
        setExportStatus(FAILED);
      }
    }
  };

  const handleExportResult = () => {
    setShowExportConfirmation(false);
    setExportType(null);
    setExportStatus(IDLE);
  };

  /**
   * Gets the confirmation description based on export type
   * @returns {string} Confirmation message for the modal
   */
  const getExportConfirmationMessage = () => {
    if (exportType === 'CSV') {
      return 'Are you sure you want to export the submission sessions to a CSV file? This will include all filtered records with headers: Course, Quest, SubQuest, SubQuestType, Name, FileName, Score, Remarks.';
    }
    if (exportType === 'ZIP') {
      return 'Are you sure you want to export the file submissions to a Zip file? This will create a zip folder with designated folders for each submission labeled with file names.';
    }
    return '';
  };

  /**
   * Gets the success message based on export type
   * @returns {string} Success message for the modal
   */
  const getExportSuccessMessage = () => {
    if (exportType === 'CSV') {
      return 'CSV export completed successfully! Your file has been downloaded.';
    }
    if (exportType === 'ZIP') {
      if (zipStats) {
        const { totalFiles, successful, failed } = zipStats;
        if (failed > 0) {
          return `Zip file export completed with warnings! ${successful} of ${totalFiles} files downloaded successfully. ${failed} file(s) failed to download.`;
        }
        return `Zip file export completed successfully! ${successful} file(s) have been downloaded and organized by user.`;
      }
      return 'Zip file export completed successfully! Your file has been downloaded.';
    }
    return '';
  };

  return (
    <>
      <Modal
        isOpen={exportSubmissionSessions.modal}
        onClose={() => handleCloseExportModal()}
        size="2xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Export File Submissions</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex mb={10} direction="column" gap={5}>
              {/* FILTERS SECTION */}
              <Text fontWeight="bold">FILTERS</Text>
              <Flex gap={5}>
                <FormikFormControl label="Course">
                  <AsyncSelect
                    placeholder="Search course"
                    loadOptions={loadCourses}
                    value={selectedCourse}
                    onChange={handleCourseChange}
                    isClearable
                  />
                </FormikFormControl>
                <FormikFormControl label="Quest">
                  <AsyncSelect
                    placeholder="Search quest"
                    loadOptions={loadQuests}
                    value={selectedQuest}
                    onChange={handleQuestChange}
                    isClearable
                  />
                </FormikFormControl>
              </Flex>
              <Flex gap={5}>
                <FormikFormControl label="Status">
                  <Select
                    placeholder="Select status"
                    options={statusOptions}
                    value={selectedStatus}
                    onChange={handleStatusChange}
                    isMulti
                    isClearable
                  />
                </FormikFormControl>
              </Flex>

              <Button
                alignSelf="flex-end"
                size="sm"
                maxWidth="250px"
                colorScheme="red"
                onClick={handleApplyFilters}
                isLoading={
                  exportSubmissionSessions.status === ReducerStatus.LOADING
                }
              >
                Apply Filters
              </Button>
            </Flex>

            <Divider my={6} />

            {/* RECORDS TO EXPORT SUMMARY PREVIEW */}
            <Flex direction="column">
              {/* Header with Toggle */}
              <Flex align="center" justify="space-between" mb={4}>
                <Text fontWeight="bold">RECORDS TO EXPORT</Text>
                <IconButton
                  icon={showSummary ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  onClick={() => setShowSummary(!showSummary)}
                  size="sm"
                  variant="ghost"
                  aria-label={showSummary ? 'Hide summary' : 'Show summary'}
                />
              </Flex>

              {/* Collapsible Summary Content */}
              <Collapse in={showSummary} animateOpacity>
                <Box
                  p={6}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={borderColor}
                  bg={cardBg}
                >
                  {/* Loading State */}
                  {exportSubmissionSessions.status ===
                    ReducerStatus.LOADING && (
                    <Box minHeight="150px">
                      <LoadingSpinner
                        size="md"
                        message="Loading records preview..."
                      />
                    </Box>
                  )}

                  {/* Summary Data Display */}
                  {exportSubmissionSessions.status ===
                    ReducerStatus.SUCCEEDED &&
                    summaryData && (
                      <VStack align="stretch" spacing={4}>
                        {/* Total Records */}
                        <Flex align="center" gap={2}>
                          <Text fontSize="lg" fontWeight="semibold">
                            Total Records:
                          </Text>
                          <Badge colorScheme="blue" fontSize="lg" px={3} py={1}>
                            {summaryData.totalRecords}
                          </Badge>
                        </Flex>

                        {/* Status Breakdown */}
                        <Box>
                          <Text fontSize="sm" fontWeight="semibold" mb={2}>
                            Breakdown by Status:
                          </Text>
                          <VStack align="stretch" spacing={2} pl={4}>
                            {summaryData.statusBreakdown.map((item) => (
                              <Flex
                                key={item.status}
                                align="center"
                                justify="space-between"
                              >
                                <Flex align="center" gap={2}>
                                  <Badge
                                    colorScheme={item.colorScheme}
                                    minW="80px"
                                  >
                                    {item.status}
                                  </Badge>
                                  <Text fontSize="sm" color={helperTextColor}>
                                    {item.count} records ({item.percentage}%)
                                  </Text>
                                </Flex>
                              </Flex>
                            ))}
                          </VStack>
                        </Box>

                        {/* Type Breakdown */}
                        <Box>
                          <Text fontSize="sm" fontWeight="semibold" mb={2}>
                            Breakdown by Type:
                          </Text>
                          <VStack align="stretch" spacing={2} pl={4}>
                            {summaryData.typeBreakdown.map((item) => (
                              <Flex key={item.type} align="center" gap={2}>
                                <Text fontSize="sm">•</Text>
                                <Text fontSize="sm">{item.type}:</Text>
                                <Badge colorScheme="gray">{item.count}</Badge>
                              </Flex>
                            ))}
                          </VStack>
                        </Box>
                      </VStack>
                    )}
                </Box>
              </Collapse>
            </Flex>

            <Divider my={6} />

            {/* EXPORT OPTIONS SECTION */}
            <Flex direction="column">
              <Text fontWeight="bold" mb={4}>
                EXPORT OPTIONS
              </Text>
              <SimpleGrid columns={2} spacing={4}>
                {/* Export CSV Card */}
                <Box
                  cursor="pointer"
                  onClick={handleExportCSV}
                  p={6}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={borderColor}
                  bg={cardBg}
                  _hover={{
                    bg: hoverBgColor,
                    borderColor: 'red.300',
                    transition: 'all 0.2s ease',
                  }}
                  textAlign="center"
                >
                  <VStack spacing={3}>
                    <Icon as={FaFileCsv} boxSize={10} color="red.500" />
                    <Text fontWeight="semibold" fontSize="md">
                      Export CSV
                    </Text>
                    <Text
                      fontSize="xs"
                      color={helperTextColor}
                      lineHeight="1.5"
                    >
                      Export the submission sessions to a CSV file with headers:
                      Course, Quest, SubQuest, SubQuestType, Name, FileName,
                      Score, Remarks
                    </Text>
                  </VStack>
                </Box>

                {/* Export Zip File Card */}
                <Box
                  cursor="pointer"
                  onClick={handleExportZipFile}
                  p={6}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={borderColor}
                  bg={cardBg}
                  _hover={{
                    bg: hoverBgColor,
                    borderColor: 'red.300',
                    transition: 'all 0.2s ease',
                  }}
                  textAlign="center"
                >
                  <VStack spacing={3}>
                    <Icon as={MdFolderZip} boxSize={10} color="red.500" />
                    <Text fontWeight="semibold" fontSize="md">
                      Export Zip File
                    </Text>
                    <Text
                      fontSize="xs"
                      color={helperTextColor}
                      lineHeight="1.5"
                    >
                      Export the file submissions into a zip folder with
                      designated folders for each submission labeled with file
                      names
                    </Text>
                  </VStack>
                </Box>
              </SimpleGrid>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="gray"
              mr={3}
              px={10}
              onClick={() => handleCloseExportModal()}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Export Confirmation Modal */}
      <ConfirmationActionModal
        isVisible={showExportConfirmation}
        onClose={handleCancelExport}
        onConfirm={handleConfirmExport}
        onSubmitResult={handleExportResult}
        status={exportStatus}
        description={getExportConfirmationMessage()}
        successMessage={getExportSuccessMessage()}
        error={exportError}
      >
        {/* Show progress during ZIP creation */}
        {exportStatus === LOADING &&
          exportType === 'ZIP' &&
          zipProgress.total > 0 && (
            <Box pb={4}>
              <LoadingSpinner
                message={`Downloading file ${zipProgress.current} of ${zipProgress.total}...`}
              />
            </Box>
          )}
      </ConfirmationActionModal>
    </>
  );
}

export default ExportFileSessionsMd;
